import React from "react";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  FormControlLabel,
  Box,
  Tooltip,
} from "@mui/material";
import FantasyDriverDetailsDrawer from "./FantasyDriverDetailsDrawer";
import { getAverageFeatureValue } from "../utils/raceUtils";
import { useSortedDrivers } from "../hooks/useSortedDrivers";
import { useDriverCarNumbers } from "../hooks/useDriverCarNumbers";
import { useDriverManufacturers } from "../hooks/useDriverManufacturers";
import { useFantasyDrawer } from "./hooks/useFantasyDrawer";
import { tableContainerSx } from "../styles/tableStyles";
import { stickyOnPhone, phoneNumberColumn, PHONE_NUMBER_COLUMN } from "../styles/responsive";
import ManufacturerLogo from "./ManufacturerLogo";

const FantasyOverviewTable = ({ groupDrivers, trackRaces, similarTrackRaces, allRaces, pastSeasonRaces, currentSeasonRaces }) => {
  const excludePlayoffs = false;
  const [excludeDnf, setExcludeDnf] = React.useState(false);

  const { selectedDriver, handleDriverClick, closeDrawer } = useFantasyDrawer();
  const driverCarNumbers = useDriverCarNumbers(allRaces || currentSeasonRaces);
  const driverManufacturers = useDriverManufacturers(allRaces || currentSeasonRaces);
  const sortedDrivers = useSortedDrivers(groupDrivers, trackRaces, "race_pos", excludePlayoffs, excludeDnf);

  return (
    <Paper sx={{ borderRadius: 3, p: 3, boxShadow: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <FormControlLabel
            control={<Checkbox checked={excludeDnf} onChange={() => setExcludeDnf(!excludeDnf)} />}
            label="Exclude DNFs"
          />
        </Box>
      </Box>

      <TableContainer sx={tableContainerSx}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200' }}>
              <TableCell rowSpan={2} sx={[{ fontWeight: "bold", px: 2 }, phoneNumberColumn, stickyOnPhone(0, { header: true })]}>#</TableCell>
              <TableCell rowSpan={2} sx={[{ fontWeight: "bold", px: 2 }, stickyOnPhone(PHONE_NUMBER_COLUMN, { header: true })]}>Driver</TableCell>
              <Tooltip title={`Average finish position at this specific track`}>
                <TableCell sx={{
                  fontWeight: "bold",
                  textAlign: "center",
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? '#4F4F4F' : 'grey.300',
                }}>
                  Avg Finish
                </TableCell>
              </Tooltip>
              <Tooltip title={`Average starting position at this specific track`}>
                <TableCell sx={{
                  fontWeight: "bold",
                  textAlign: "center",
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? '#4F4F4F' : 'grey.300',
                }}>
                  Avg Start
                </TableCell>
              </Tooltip>
              <Tooltip title={`Average fantasy points earned at this specific track`}>
                <TableCell sx={{
                  fontWeight: "bold",
                  textAlign: "center",
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? '#4F4F4F' : 'grey.300',
                }}>
                  Avg Fantasy
                </TableCell>
              </Tooltip>
              <Tooltip title={`Average race rating at this specific track`}>
                <TableCell sx={{
                  fontWeight: "bold",
                  textAlign: "center",
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? '#4F4F4F' : 'grey.300',
                }}>
                  Avg Rating
                </TableCell>
              </Tooltip>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedDrivers.map((driver, index) => (
              <TableRow
                key={index}
                sx={{
                  bgcolor: index % 2 === 0 ? "background.default" : "action.hover",
                  "&:hover": { bgcolor: "action.selected", transition: "all 0.3s ease" },
                }}
              >
                <TableCell sx={[{ width: "40px", fontWeight: "bold", px: 2, cursor: "pointer" }, phoneNumberColumn, stickyOnPhone(0, { odd: index % 2 === 1 })]}>{driverCarNumbers[driver] || "-"}</TableCell>
                <TableCell sx={[{ fontWeight: "bold", px: 2, cursor: "pointer" }, stickyOnPhone(PHONE_NUMBER_COLUMN, { odd: index % 2 === 1 })]} onClick={() => handleDriverClick(driver)}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ManufacturerLogo manufacturer={driverManufacturers[driver]} />
                    {driver}
                  </Box>
                </TableCell>
                <Tooltip title={`${driver}'s average finish position specifically at this track`}>
                  <TableCell sx={{
                    textAlign: "center",
                    bgcolor: (theme) => theme.palette.mode === 'dark'
                      ? index % 2 === 0 ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.05)'
                      : index % 2 === 0 ? 'rgba(0, 0, 0, 0.03)' : 'rgba(0, 0, 0, 0.04)',
                  }}>
                    {getAverageFeatureValue(trackRaces, driver, excludePlayoffs, excludeDnf, "race_pos")}
                  </TableCell>
                </Tooltip>
                <Tooltip title={`${driver}'s average starting position specifically at this track`}>
                  <TableCell sx={{
                    textAlign: "center",
                    bgcolor: (theme) => theme.palette.mode === 'dark'
                      ? index % 2 === 0 ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.05)'
                      : index % 2 === 0 ? 'rgba(0, 0, 0, 0.03)' : 'rgba(0, 0, 0, 0.04)',
                  }}>
                    {getAverageFeatureValue(trackRaces, driver, excludePlayoffs, excludeDnf, "quali_pos")}
                  </TableCell>
                </Tooltip>
                <Tooltip title={`${driver}'s average fantasy points specifically at this track`}>
                  <TableCell sx={{
                    textAlign: "center",
                    bgcolor: (theme) => theme.palette.mode === 'dark'
                      ? index % 2 === 0 ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.05)'
                      : index % 2 === 0 ? 'rgba(0, 0, 0, 0.03)' : 'rgba(0, 0, 0, 0.04)',
                  }}>
                    {getAverageFeatureValue(trackRaces, driver, excludePlayoffs, excludeDnf, "fantasy_points")}
                  </TableCell>
                </Tooltip>
                <Tooltip title={`${driver}'s average race rating specifically at this track`}>
                  <TableCell sx={{
                    textAlign: "center",
                    bgcolor: (theme) => theme.palette.mode === 'dark'
                      ? index % 2 === 0 ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.05)'
                      : index % 2 === 0 ? 'rgba(0, 0, 0, 0.03)' : 'rgba(0, 0, 0, 0.04)',
                  }}>
                    {getAverageFeatureValue(trackRaces, driver, excludePlayoffs, excludeDnf, "driver_rating")}
                  </TableCell>
                </Tooltip>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedDriver && (
        <FantasyDriverDetailsDrawer
          driver={selectedDriver}
          groupDrivers={groupDrivers}
          trackRaces={trackRaces}
          similarTrackRaces={similarTrackRaces}
          allRaces={allRaces}
          pastSeasonRaces={pastSeasonRaces}
          currentSeasonRaces={currentSeasonRaces}
          onClose={closeDrawer}
        />
      )}
    </Paper>
  );
};

export default FantasyOverviewTable;
